import { createSelector } from '@reduxjs/toolkit';
import { selectFeatureEnabled } from 'reduxStore/featureFlags/selectors';

export const ROOM_STAYS_FLAG = 'credentials.roomStays';

/** New-feature gating: hidden until the flag is known ON (INV-13). */
export const selectRoomStaysEnabled = (state) => selectFeatureEnabled(state, ROOM_STAYS_FLAG, false);

const selectSlice = (state) => state.RoomStays || {};
const EMPTY = [];
/**
 * Every holder entry GET /room-stays/holders?includeRetiredGenerated=true returned: live holders (`retired: false`) and
 * one entry per person whose only holder rows are retired GENERATED ones (`retired: true`). Only the exclusion set and
 * the People tags read this; everything that acts on a holder reads `selectRoomHolders` (live only).
 */
const selectHolderEntries = (state) => selectSlice(state).holders || EMPTY;
/** The org's LIVE holders. A retired generated holder is not a room, stock or store any more, so nothing acts on it. */
export const selectRoomHolders = createSelector([selectHolderEntries], (holders) =>
	holders.filter((h) => h.retired !== true)
);
export const selectHoldersLoaded = (state) => Boolean(selectSlice(state).holdersLoaded);
export const selectHoldersError = (state) => selectSlice(state).holdersError || null;
/** The card whose stay staff confirm now (oldest first), and how many cards are waiting in all. */
export const selectIssueConfirmation = (state) => selectSlice(state).issueConfirmations?.[0] || null;
export const selectIssueConfirmationCount = (state) => (selectSlice(state).issueConfirmations || EMPTY).length;
export const selectStaysLoadError = (state) => selectSlice(state).error || null;
export const selectStaysLoading = (state) => Boolean(selectSlice(state).staysLoading);
/** The last stays READ's failure, separate from any change's refusal. */
export const selectStaysError = (state) => selectSlice(state).staysError || null;
/** The server's answer to the last stay change: `{ roomStayID, ok, error, operation }`, or null. */
export const selectLastStayChange = (state) => selectSlice(state).lastChange || null;
export const selectRoomStaysMutating = (state) => Boolean(selectSlice(state).mutating);

/** personID → holder, for the designated holders the caller can see. */
export const selectHolderByPersonID = createSelector([selectRoomHolders], (holders) =>
	holders.reduce((acc, h) => ({ ...acc, [h.personID]: h }), {})
);

const NO_IDS = new Set();

/**
 * Room cards setup §5.1.2 (R6, INV-06): the people every list, picker and count hides — every live holder AND every
 * retired generated holder the caller can see, while the flag is on. A retired HAND-designated holder is a real person:
 * the server does not return it, so it reappears. Empty (one stable Set) while the flag is off.
 */
export const selectHiddenHolderPersonIDs = createSelector(
	[selectRoomStaysEnabled, selectHolderEntries],
	(on, holders) => (on && holders.length ? new Set(holders.map((h) => h.personID)) : NO_IDS)
);

/** personID → holder entry, live or retired generated: what the People tag reads when "Show card holders" is on. */
export const selectHolderTagByPersonID = createSelector([selectHolderEntries], (holders) =>
	holders.reduce((acc, h) => (acc[h.personID] && !acc[h.personID].retired ? acc : { ...acc, [h.personID]: h }), {})
);

/**
 * Room cards setup R1: setup preview/apply, panel users, "Read locks now" and Retire are Global Admin (101) or
 * Developer (201) on the server (holderAdminOnly). A Site Admin (103) reaches the page for the board, stays and
 * repair, and sees setup read-only. The server is authoritative; this decides what to SHOW.
 * Roles only: the profile's `isPlatformDeveloper` flag is NOT a holder admin, because the server's holderAdminOnly
 * reads role 201 (req.state.isDeveloper), so showing the editor on the flag alone would offer writes that answer 403.
 */
const HOLDER_ADMIN_ROLES = [101, 201];
export const selectIsRoomHolderAdmin = (state) =>
	Object.keys(state.Auth?.selectedProfile?.roles || {}).some((id) => HOLDER_ADMIN_ROLES.includes(Number(id)));

/**
 * Whether this caller may WRITE stays and issue or return cards on `siteID` — the server's `stayWriteScope`
 * (helpers/roomStays/access.js) read from the profile: Global Admin or Developer org-wide, otherwise exactly the sites
 * where they hold Site Admin (103). Asset Admin, Site Lock Admin, read-only roles and installers write nothing, so the
 * generic `aCPermissionManagement` gate (which admits several of them) is too wide here. The server stays authoritative.
 */
const SITE_ADMIN_ROLE = 103;
export const canWriteRoomStays = (roles, siteID) => {
	const map = roles || {};
	if (Object.keys(map).some((id) => HOLDER_ADMIN_ROLES.includes(Number(id)))) return true;
	const sites = map[SITE_ADMIN_ROLE];
	return siteID != null && Array.isArray(sites) && sites.map(Number).includes(Number(siteID));
};
export const selectCanWriteRoomStays = (state, siteID) => canWriteRoomStays(state.Auth?.selectedProfile?.roles, siteID);
/** Whether the caller may write stays on at least one of `siteIDs` (the page's Check in under All sites). */
export const selectCanWriteAnyRoomStays = (state, siteIDs) =>
	(siteIDs || []).some((id) => canWriteRoomStays(state.Auth?.selectedProfile?.roles, id));

/** Why a stay or card write is unavailable, for the disabled control's tooltip. */
export const STAY_WRITE_DENIED =
	'Only a Global Admin or a Site Admin of this room’s site can change its stays and cards.';

/** The People page toggle; always false while the flag is off, so no stale "on" survives a flag flip. */
export const selectShowCardHolders = (state) =>
	selectRoomStaysEnabled(state) && Boolean(selectSlice(state).showHolders);

/** The room holder for this person, or null (stock and store holders have no stays). */
export const selectRoomHolderForPerson = (state, personID) => {
	const h = selectHolderByPersonID(state)[personID];
	return h && h.role === 'room' ? h : null;
};

export const selectStaysForHolder = (state, holderID) => selectSlice(state).staysByHolder?.[holderID] || EMPTY;

/** Hotel guest mode: the room holder whose issue is running (null when none), and how the last one ended. */
export const selectIssuingHolderID = (state) => selectSlice(state).issuing ?? null;
export const selectIssueResult = (state) => selectSlice(state).issueResult || null;

// ─── Room stays page (room-cards-setup §5.2) ─────────────────────────────────────────────────────────────────
const EMPTY_OBJ = Object.freeze({});
export const selectBoardForSite = (state, siteID) => selectSlice(state).board?.bySite?.[siteID] || null;
export const selectBoardStatus = (state) => selectSlice(state).board || EMPTY_OBJ;
export const selectSetupForSite = (state, siteID) => selectSlice(state).setup?.bySite?.[siteID] || null;
export const selectSetupStatus = (state) => selectSlice(state).setup || EMPTY_OBJ;
/** This site's own setup load error (null while none, or while its load is in flight). */
export const selectSetupErrorForSite = (state, siteID) => selectSlice(state).setup?.errorBySite?.[siteID] || null;
export const selectOccupancyForSite = (state, siteID) => selectSlice(state).occupancy?.bySite?.[siteID] || EMPTY;
export const selectOccupancyStatus = (state) => selectSlice(state).occupancy || EMPTY_OBJ;
export const selectPanelUsersForSite = (state, siteID) => selectSlice(state).panelUsers?.bySite?.[siteID] || EMPTY;
export const selectSetupRun = (state) => selectSlice(state).setupRun || EMPTY_OBJ;
export const selectAddRoomRun = (state) => selectSlice(state).addRoomRun || EMPTY_OBJ;
export const selectRepairing = (state) => selectSlice(state).repairing || EMPTY_OBJ;
export const selectRetiring = (state) => selectSlice(state).retiring || EMPTY_OBJ;

/** cardPoolHolderID → holder (the board names a room by holder; Manage Access needs its personID). */
export const selectHolderByHolderID = createSelector([selectRoomHolders], (holders) =>
	holders.reduce((acc, h) => ({ ...acc, [h.cardPoolHolderID]: h }), {})
);

/**
 * All sites (PLAN v0.8.5 §5.2): the sites with a live holder, from GET holders (site-scoped, so a caller sees only the
 * sites they may). These are the sites whose boards the page loads; a site with no holder has no rooms to list.
 */
export const selectSetUpSiteIDs = createSelector([selectRoomHolders], (holders) =>
	[...new Set(holders.map((h) => Number(h.siteID)).filter((id) => Number.isFinite(id)))].sort((a, b) => a - b)
);

const sumCounts = (boards) =>
	boards.reduce((acc, b) => {
		Object.entries(b.counts || {}).forEach(([k, v]) => {
			if (typeof v === 'number') acc[k] = (acc[k] || 0) + v;
		});
		return acc;
	}, {});

/**
 * Several sites' boards as one, for the All sites view: every room tagged with its `siteID` (each site's own board is
 * the only source; nothing is derived, INV-10) and the numeric counts summed. `siteKey` is the site IDs joined by ","
 * so the memo holds across renders. Null until at least one of those boards has loaded.
 */
export const selectBoardForSites = createSelector(
	[(state) => selectSlice(state).board?.bySite || EMPTY_OBJ, (_, siteKey) => siteKey],
	(bySite, siteKey) => {
		const ids = String(siteKey || '')
			.split(',')
			.filter(Boolean)
			.map(Number);
		const boards = ids.map((id) => bySite[id] && { siteID: id, ...bySite[id] }).filter(Boolean);
		if (!boards.length) return null;
		return {
			siteIDs: boards.map((b) => b.siteID),
			rooms: boards.flatMap((b) => (b.rooms || []).map((r) => ({ ...r, siteID: b.siteID }))),
			counts: sumCounts(boards)
		};
	}
);
